// ── TierFeaturesPopover (issue #786) ───────────────────────────────────────
// Centred modal that lists what's included and excluded for the given
// tier. Originally implemented as a position:absolute popover anchored to
// the info icon, but the content list grew long enough that it overflowed
// the viewport when the user had scrolled the Settings page below the
// Plan card (top of the popover went above the viewport, cropped). The
// centred-modal pattern guarantees viewport-bounding regardless of where
// the user is on the page, with internal scroll on overflow.
//
// Accessible: role="dialog", aria-modal, aria-labelledby on the heading,
// focus moves to the dialog on open and returns to the trigger on close.
// Backdrop click + Escape + Close button all dismiss.

import { useEffect, useRef } from 'react';
import { getPlanDisplayName, getTierFeatures } from '@blueprint/shared';

interface Props {
  /** Canonical tier value from MyTenant. */
  tier: string | null | undefined;
  /** Element that triggered the modal — used to return focus on close. */
  triggerRef: React.RefObject<HTMLElement | null>;
  /** Closes the modal. Called on Escape, backdrop click, and Close button. */
  onClose: () => void;
}

const backdropStyle: React.CSSProperties = {
  position:        'fixed',
  inset:           0,
  zIndex:          50,
  background:      'rgba(0,0,0,0.45)',
  display:         'flex',
  alignItems:      'center',
  justifyContent:  'center',
  padding:         16,
};

const dialogStyle: React.CSSProperties = {
  position:      'relative',
  width:         'min(520px, 100%)',
  maxHeight:     'min(80vh, 720px)',
  display:       'flex',
  flexDirection: 'column',
  background:    'var(--surface)',
  border:        '1px solid var(--border)',
  borderRadius:  'var(--radius-lg)',
  boxShadow:     'var(--shadow-md, 0 10px 30px rgba(0,0,0,0.35))',
  fontFamily:    'var(--font-body)',
  fontSize:      13,
  color:         'var(--text)',
  // Inner scroll: header + footer stay fixed; only the list area scrolls
  // when content exceeds the dialog height.
  overflow:      'hidden',
};

const headerStyle: React.CSSProperties = {
  padding:       '16px 20px 12px',
  borderBottom:  '1px solid var(--border)',
  flexShrink:    0,
};

const headingStyle: React.CSSProperties = {
  fontFamily:    'var(--font-display)',
  fontSize:      16,
  fontWeight:    600,
  color:         'var(--text)',
  margin:        0,
};

const bodyStyle: React.CSSProperties = {
  padding:    '12px 20px 16px',
  overflowY:  'auto',
  flexGrow:   1,
};

const sectionLabelStyle: React.CSSProperties = {
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  fontSize:      11,
  fontWeight:    600,
  color:         'var(--text-secondary)',
  margin:        '8px 0 6px 0',
};

const listStyle: React.CSSProperties = {
  margin:      0,
  paddingLeft: 0,
  listStyle:   'none',
};

const itemStyle: React.CSSProperties = {
  display:    'flex',
  alignItems: 'flex-start',
  gap:        8,
  padding:    '6px 0',
  lineHeight: 1.45,
};

const footerStyle: React.CSSProperties = {
  padding:       '12px 20px 16px',
  borderTop:     '1px solid var(--border)',
  flexShrink:    0,
  display:       'flex',
  justifyContent: 'flex-end',
};

const closeBtnStyle: React.CSSProperties = {
  padding:      '6px 14px',
  fontFamily:   'var(--font-display)',
  fontSize:     12,
  fontWeight:   600,
  color:        'var(--text)',
  background:   'transparent',
  border:       '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)',
  cursor:       'pointer',
};

export default function TierFeaturesPopover({ tier, triggerRef, onClose }: Props) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const features  = getTierFeatures(tier);
  const planName  = getPlanDisplayName(tier);
  const headingId = 'tier-features-heading';

  // Focus management — move focus into the dialog on mount, return it to
  // the trigger on unmount.
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    return () => {
      if (triggerRef.current) {
        triggerRef.current.focus();
      } else if (previouslyFocused) {
        previouslyFocused.focus();
      }
    };
  }, [triggerRef]);

  // Close on Escape. Backdrop click is handled directly on the backdrop
  // div below (more reliable than a global listener that has to ignore
  // the trigger).
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Backdrop receives the click only when the user clicks outside the
    // dialog (the dialog stops propagation below).
    if (e.target === e.currentTarget) onClose();
  };

  // Empty lists → nothing meaningful to show. Render a minimal "no info"
  // fallback rather than a confusing empty modal.
  if (features.included.length === 0 && features.excluded.length === 0) {
    return (
      <div
        style={backdropStyle}
        onClick={handleBackdropClick}
        onMouseDown={handleBackdropClick}
        data-testid="tier-features-backdrop"
      >
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={headingId}
          tabIndex={-1}
          style={dialogStyle}
          onMouseDown={(e) => e.stopPropagation()}
          data-testid="tier-features-popover"
        >
          <div style={headerStyle}>
            <h3 id={headingId} style={headingStyle}>{planName}</h3>
          </div>
          <div style={bodyStyle}>
            <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
              No feature details available for this plan.
            </p>
          </div>
          <div style={footerStyle}>
            <button type="button" onClick={onClose} style={closeBtnStyle} data-testid="tier-features-close">
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={backdropStyle}
      onClick={handleBackdropClick}
      onMouseDown={handleBackdropClick}
      data-testid="tier-features-backdrop"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        tabIndex={-1}
        style={dialogStyle}
        onMouseDown={(e) => e.stopPropagation()}
        data-testid="tier-features-popover"
      >
        <div style={headerStyle}>
          <h3 id={headingId} style={headingStyle}>What&rsquo;s in {planName}</h3>
        </div>

        <div style={bodyStyle}>
          {features.included.length > 0 && (
            <>
              <div style={sectionLabelStyle}>Included</div>
              <ul style={listStyle} data-testid="tier-features-included">
                {features.included.map((f) => (
                  <li key={f} style={itemStyle}>
                    <span style={{ color: 'var(--success)', fontWeight: 700, flexShrink: 0, marginTop: 1 }} aria-hidden>✓</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </>
          )}

          {features.excluded.length > 0 && (
            <>
              <div style={sectionLabelStyle}>Not included</div>
              <ul style={listStyle} data-testid="tier-features-excluded">
                {features.excluded.map((f) => (
                  <li key={f} style={itemStyle}>
                    <span style={{ color: 'var(--text-secondary)', fontWeight: 700, flexShrink: 0, marginTop: 1 }} aria-hidden>✗</span>
                    <span style={{ color: 'var(--text-secondary)' }}>{f}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        <div style={footerStyle}>
          <button type="button" onClick={onClose} style={closeBtnStyle} data-testid="tier-features-close">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
